import { useEffect, useRef } from 'react';
import type { DataSchema, Schema } from 'types';
import { processDataToFormData } from 'components/common/Table/tableFunctions';
import type { TableFormData, TableFormDataRow } from 'components/common/Table/Table';
import type { SetValueConfig } from 'react-hook-form';
import type { ErrorOption } from 'react-hook-form';
import type { Snapshot } from '../../contexts/FormRegistryProvider.tsx';

interface UseTableFormSyncProps<T extends keyof P, P extends DataSchema> {
  data: Schema<T, P>[];
  dataSchema: P;
  forceReset: boolean | undefined;
  initialSnapshot: Snapshot<T, P> | undefined;
  getValues: () => TableFormData;
  reset: (
    values: TableFormData,
    flags: {
      keepErrors: boolean;
      keepDirty: boolean;
      keepTouched: boolean;
      keepDirtyValues: boolean;
      keepIsValid: boolean;
    }
  ) => void;
  setValue: (key: string, value: TableFormDataRow, options?: SetValueConfig) => void;
  setError: (name: string, error: ErrorOption) => void;
  onFormStateReset?: () => void;
  paginator: { pageIndex: number; pageSize: number };
}

export function useTableFormSync<T extends keyof P, P extends DataSchema>(p: UseTableFormSyncProps<T, P>): void {
  const { data, dataSchema, forceReset, initialSnapshot, getValues, reset, onFormStateReset, setValue, setError } = p;

  const hydratedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!hydratedRef.current) {
      hydratedRef.current = true;

      if (initialSnapshot && initialSnapshot.values) {
        reset(initialSnapshot.values, {
          keepErrors: false,
          keepDirty: initialSnapshot.isDirty,
          keepTouched: true,
          keepDirtyValues: initialSnapshot.isDirty,
          keepIsValid: false,
        });

        if (initialSnapshot.dirtyFields) {
          Object.keys(initialSnapshot.dirtyFields).forEach((field) => {
            setValue(field, initialSnapshot.values[field], { shouldDirty: true });
          });
        }

        if (initialSnapshot.errors) {
          Object.entries(initialSnapshot.errors).forEach(([rowId, rowErrors]) => {
            if (rowErrors && typeof rowErrors === 'object') {
              Object.entries(rowErrors).forEach(([cellId, cellError]) => {
                if (cellError && typeof cellError === 'object' && 'type' in cellError) {
                  const fieldPath = `${rowId}.${cellId}`;
                  setError(fieldPath, cellError as ErrorOption);
                }
              });
            }
          });
        }

        return;
      }
    }

    const baseValues = forceReset ? {} : getValues();
    const formData = processDataToFormData(data, baseValues, dataSchema);

    reset(formData, {
      keepErrors: true,
      keepDirty: !forceReset,
      keepTouched: true,
      keepDirtyValues: !forceReset,
      keepIsValid: false,
    });

    if (forceReset) onFormStateReset?.();
  }, [data, dataSchema, forceReset, initialSnapshot, getValues, reset, onFormStateReset, setValue, setError]);
}
